import type { ReactNode } from 'react'
import styles from './DocumentCreationModal.module.css'
import {
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheet,
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
  ModalSheetContent,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { useDropzone } from '@/hooks/dropzone-hook'
import { FileButton } from '@/components/FileButton'

export interface DocumentCreationModalProps {
  showing: boolean
  onClose: () => unknown
  dragging: boolean
  onAddFiles: (files: FileList) => void
  onDragEnter: () => void
  onDragLeave: () => void
  uploadDisabled: boolean
  isUploadPending: boolean
  onUpload: () => unknown
  overlay: ReactNode
  /**
   * One entry per staged file.
   */
  children?: ReactNode
}

export function DocumentCreationModal({
  showing,
  onClose,
  dragging,
  onAddFiles,
  onDragEnter,
  onDragLeave,
  uploadDisabled,
  isUploadPending,
  onUpload,
  overlay,
  children,
}: DocumentCreationModalProps) {
  const { dropzoneProps } = useDropzone<HTMLDivElement>({
    onFilesDropped: onAddFiles,
    onEnter: onDragEnter,
    onLeave: onDragLeave,
  })
  return (
    <ModalSheet
      dropzoneProps={dropzoneProps}
      showing={showing}
      disableCloseOnOutsideClick={true}
      onRequestClose={isUploadPending ? undefined : onClose}
    >
      {overlay}
      <ModalSheetHeaderShell size="auto" bordered>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerInfo}>
              <div className={styles.headerTitle}>
                <span>Assign file type</span>
              </div>
              <div className={styles.headerDescription}>
                <span>Select an appropriate document type for each file</span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.closeBtn}>
          <ModalSheetHeaderCloseButton />
        </div>
      </ModalSheetHeaderShell>
      <ModalSheetContent>
        <div className={styles.creationItemList}>
          {children}
          <div
            className={
              dragging ? styles.dropZoneItemDragging : styles.dropZoneItem
            }
          >
            <span className={styles.dropZoneTitle}>
              Drag and drop files here or&nbsp;
              <FileButton multiple onChange={onAddFiles}>
                <Button
                  label="Upload another file"
                  className={styles.dropZoneButton}
                  borderless
                  variant="text"
                />
              </FileButton>
            </span>
          </div>
        </div>
      </ModalSheetContent>
      <ModalSheetFooter>
        <div className={styles.footer}>
          <div className={styles.uploadButton}>
            <ModalSheetFooterButtonGroup>
              <Button
                label="Upload Documents"
                variant="primary"
                isDisabled={uploadDisabled}
                isPending={isUploadPending}
                onPress={() => onUpload()}
              />
            </ModalSheetFooterButtonGroup>
          </div>
        </div>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
